import RowActions, { TableRowAction } from 'components/controls/table-row-actions';
import ToolBarActions from 'components/controls/table-toolbar-actions';
import { SORTING_INITIAL_STATE, TABLE_SETTINGS } from 'constants/table-settings';
import {
    MRT_ColumnDef,
    MRT_ColumnFiltersState,
    MRT_PaginationState,
    MRT_RowData,
    MRT_SortingState,
    useMaterialReactTable,
} from 'material-react-table';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { BaseQueryParams } from 'types/base-query-params';
import { TableQueryIdentifier } from 'types/table-settings';
import { getParams } from 'utils/params-helpers';
import {
    formatColumnsFilters,
    formatColumnsSorting,
    generateAddUrl,
    generateEditUrl,
    getQueryFunction,
} from 'utils/table-helpers';

type UseTableProps<T extends MRT_RowData> = {
    columns: MRT_ColumnDef<T>[];
    queryIdentifier: TableQueryIdentifier;
    enableRowActions?: boolean;
    renderAddButton?: boolean;
    onRowClick?: (row: T) => void;
    customActions?: TableRowAction[];
};

const useTable = <T extends MRT_RowData>({
    columns,
    queryIdentifier,
    enableRowActions = true,
    renderAddButton = true,
    onRowClick = undefined,
    customActions = [],
}: UseTableProps<T>) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { placeId = undefined } = useParams();

    const { fetchQuery, deleteMutation } = getQueryFunction(queryIdentifier);
    const enableDelete = deleteMutation ? true : false;
    const [searchParams, setSearchParams] = useSearchParams();

    const { currentPage, sortingFromUrl, filtersFromUrl } = getParams(searchParams);
    const [pagination, setPagination] = useState<MRT_PaginationState>({
        pageIndex: currentPage,
        pageSize: 10,
    });
    const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>(filtersFromUrl);
    const [sorting, setSorting] = useState<MRT_SortingState>(sortingFromUrl ?? SORTING_INITIAL_STATE);

    useEffect(() => {
        setSearchParams((prevParams) => {
            const updatedParams = new URLSearchParams(prevParams);

            updatedParams.set('page', String(pagination.pageIndex + 1));
            updatedParams.set('size', String(pagination.pageSize));

            for (const key of [...updatedParams.keys()]) {
                if (key !== 'size' && key !== 'page') {
                    updatedParams.delete(key);
                }
            }

            const sortParams = formatColumnsSorting(sorting);
            Object.entries(sortParams).forEach(([key, value]) => {
                if (value) {
                    updatedParams.set(`sort.${key}`, String(value));
                }
            });

            const filters = formatColumnsFilters(columnFilters);
            Object.entries(filters).forEach(([key, value]) => {
                if (value) {
                    updatedParams.set(key, String(value));
                }
            });

            return updatedParams.toString() === prevParams.toString() ? prevParams : updatedParams;
        });
    }, [pagination, columnFilters, setSearchParams, sorting]);

    const params = {
        page: pagination.pageIndex + 1,
        size: pagination.pageSize,
        ...formatColumnsFilters(columnFilters),
        sort: { ...formatColumnsSorting(sorting) },
    } as BaseQueryParams;

    const { data, isLoading } = fetchQuery(placeId ? { ...params, placeId } : params);

    const handleAddRow = useCallback(() => {
        navigate(generateAddUrl(queryIdentifier));
    }, [navigate, queryIdentifier]);

    const handleEditRow = useCallback(
        (id: string) => {
            navigate(generateEditUrl(queryIdentifier, id, placeId));
        },
        [navigate, queryIdentifier, placeId],
    );

    const handleDeleteRow = useCallback(
        async (id: string) => {
            if (!deleteMutation) return;

            const [deleteItem] = deleteMutation();
            const answer = window.confirm(t('COMMON.CONFIRM_DELETE'));
            if (answer) {
                await deleteItem(id.toString());
            }
        },
        [deleteMutation, t],
    );

    const updatePagination = useCallback(
        (newPagination: MRT_PaginationState | ((old: MRT_PaginationState) => MRT_PaginationState)) => {
            setPagination((prev) => {
                const updatedPagination = typeof newPagination === 'function' ? newPagination(prev) : newPagination;

                return updatedPagination;
            });
        },
        [],
    );

    const handleColumnFiltersChange = useCallback(
        (newFilters: MRT_ColumnFiltersState | ((old: MRT_ColumnFiltersState) => MRT_ColumnFiltersState)) => {
            setColumnFilters((prev) => {
                const updatedFilters = typeof newFilters === 'function' ? newFilters(prev) : newFilters;

                return updatedFilters;
            });
        },
        [],
    );

    const tableData = useMemo(() => data?.content ?? [], [data]);

    const table = useMaterialReactTable<T>({
        columns,
        data: tableData,
        rowCount: data?.totalElements || 0,

        muiTableBodyProps: {
            sx: {
                '& .MuiTableCell-root': {
                    border: 'none',
                },
                '& tr:nth-of-type(odd) > td': {
                    backgroundColor: '#F8F9FC',
                },
            },
        },

        initialState: {
            density: 'compact',
            showColumnFilters: columnFilters.length !== 0,
        },
        state: {
            columnFilters,
            sorting,
            isLoading: isLoading,
            pagination,
        },

        onPaginationChange: updatePagination,
        onSortingChange: setSorting,
        onColumnFiltersChange: handleColumnFiltersChange,
        renderTopToolbarCustomActions: () => (
            <ToolBarActions handleAddRow={handleAddRow} renderAddButton={renderAddButton} />
        ),

        renderRowActions: ({ row }) => {
            const id = row.original.id;

            return (
                <RowActions
                    id={id}
                    handleDeleteRow={handleDeleteRow}
                    handleEditRow={handleEditRow}
                    enableDelete={enableDelete}
                    customActions={customActions}
                />
            );
        },

        muiTableBodyRowProps: onRowClick
            ? ({ row }) => ({
                  onClick: () => onRowClick(row.original),
                  sx: {
                      cursor: 'pointer',
                      '&:hover': { backgroundColor: '#f5f5f5' },
                  },
              })
            : undefined,

        displayColumnDefOptions: {
            'mrt-row-actions': {
                header: t('COMMON.ACTIONS'),
                size: 120,
            },
        },

        ...TABLE_SETTINGS,
        enableRowVirtualization: true,
        enableRowActions: enableRowActions,
        positionActionsColumn: enableRowActions ? 'last' : undefined,

        mrtTheme: (theme) => ({
            baseBackgroundColor: theme.palette.background.default,
            draggingBorderColor: theme.palette.secondary.main,
        }),
    });

    return {
        tableSettings: table,
    };
};

export default useTable;
